import {
  Cell,
  ColumnDef,
  Row,
  RowData,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "utils";
import type { BaseTableProps } from "./types";
import {
  calculateColumnShadowClasses,
  calculateLeftOffset,
  calculateRightOffset,
  calculateTotalWidth,
  generateContainerShadowClass,
  getColumnWidth,
} from "./utils";

export function BaseTable<T extends RowData>({
  data,
  columns,
  enableRowPinning = false,
  rowPinning,
  keepPinnedRows = true,
  maxHeight,
  headerHeight = 40,
  rowHeight = 40,
  classNames = {},
  getRowId,
  onRowClick,
  rowClassName,
  isLoading = false,
}: BaseTableProps<T>) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledFromStart, setIsScrolledFromStart] = useState(false);
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);

  const calculateTableTotalWidth = useCallback((): number => {
    return calculateTotalWidth(columns);
  }, [columns]);

  const table = useReactTable({
    data,
    columns,
    state: {
      rowPinning: enableRowPinning ? rowPinning : undefined,
    },
    enableRowPinning,
    keepPinnedRows,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
  });

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const checkScrollState = () => {
      const containerWidth = container.clientWidth;
      const scrollLeft = container.scrollLeft;
      const totalWidth = calculateTableTotalWidth();

      const hasScrollableContent = totalWidth > containerWidth;
      const isAtEnd = scrollLeft + containerWidth >= totalWidth - 1;
      const isAtStart = scrollLeft <= 0;

      // 原始mask逻辑：兼容无右侧固定列的情况
      const shouldShowMask = hasScrollableContent && !isAtEnd;
      setIsScrolled(shouldShowMask);

      // 新的阴影逻辑：支持左右固定列
      setIsScrolledFromStart(hasScrollableContent && !isAtStart);
      setIsScrolledToEnd(hasScrollableContent && isAtEnd);
    };

    checkScrollState();
    container.addEventListener("scroll", checkScrollState);

    const resizeObserver = new ResizeObserver(checkScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener("scroll", checkScrollState);
      resizeObserver.disconnect();
    };
  }, [columns, rowPinning, enableRowPinning, calculateTableTotalWidth]);

  const leftPinnedColumns: ColumnDef<T, unknown>[] = columns.filter(
    (col) => col.meta?.pinned === "left",
  );

  const rightPinnedColumns: ColumnDef<T, unknown>[] = columns.filter(
    (col) => col.meta?.pinned === "right",
  );

  const topRows = enableRowPinning ? table.getTopRows() : [];
  const centerRows = enableRowPinning
    ? table.getCenterRows()
    : table.getRowModel().rows;
  const bottomRows = enableRowPinning ? table.getBottomRows() : [];

  const renderRows = (rows: Row<T>[], isPinnedRow: boolean = false) => (
    <>
      {rows.map((row, rowIdx) => {
        const rowClass =
          typeof rowClassName === "function"
            ? rowClassName(row.original)
            : rowClassName;

        const topOffset = isPinnedRow
          ? headerHeight + rowIdx * rowHeight
          : undefined;

        return (
          <tr
            data-openreplay-label="TanStackTable · BaseTable · tr"
            key={row.id}
            className={cn(
              classNames.tr,
              isPinnedRow && classNames.trPinned,
              rowClass,
            )}
            onClick={() => onRowClick?.(row.original)}
          >
            {row.getVisibleCells().map((cell: Cell<T, unknown>) => {
              const isPinnedLeft =
                cell.column.columnDef.meta?.pinned === "left";
              const isPinnedRight =
                cell.column.columnDef.meta?.pinned === "right";

              const columnIndex = table
                .getAllLeafColumns()
                .findIndex((c) => c.id === cell.column.id);

              const leftOffset = calculateLeftOffset(
                columnIndex,
                isPinnedLeft,
                leftPinnedColumns,
                columns,
              );
              const rightOffset = calculateRightOffset(
                columnIndex,
                isPinnedRight,
                rightPinnedColumns,
                columns,
              );

              const width = getColumnWidth(cell.column);

              let zIndex = "";
              if (isPinnedRow && (isPinnedLeft || isPinnedRight)) {
                zIndex = "z-[4]";
              } else if (isPinnedLeft || isPinnedRight) {
                zIndex = "z-[3]";
              } else if (isPinnedRow) {
                zIndex = "z-[2]";
              }

              // 计算列位置和阴影类名
              const { leftShadowClass, rightShadowClass } =
                calculateColumnShadowClasses(
                  cell.column.id,
                  columnIndex,
                  isPinnedLeft,
                  isPinnedRight,
                  leftPinnedColumns,
                  rightPinnedColumns,
                  columns,
                  isScrolledFromStart,
                  isScrolledToEnd,
                  classNames.mask,
                );

              const customCellClassName =
                typeof cell.column.columnDef.meta?.cellClassName === "function"
                  ? cell.column.columnDef.meta.cellClassName(row.original)
                  : cell.column.columnDef.meta?.cellClassName;

              const tdClassName = cn(
                classNames.td,
                (isPinnedLeft || isPinnedRight || isPinnedRow) &&
                  classNames.tdPinned,
                zIndex,
                customCellClassName,
                leftShadowClass,
                rightShadowClass,
              );

              return (
                <td
                  key={cell.id}
                  className={tdClassName}
                  style={{
                    ...(width && {
                      width: typeof width === "number" ? `${width}px` : width,
                      minWidth:
                        typeof width === "number" ? `${width}px` : width,
                    }),
                    ...(isPinnedLeft && { left: leftOffset }),
                    ...(isPinnedRight && { right: rightOffset }),
                    ...(isPinnedRow && { top: `${topOffset}px` }),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );

  return (
    <div className={classNames.container || ""}>
      <div
        ref={scrollContainerRef}
        className={classNames.scrollContainer || ""}
        style={{
          ...(maxHeight && { maxHeight: `${maxHeight}px`, overflowY: "auto" }),
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <table className={classNames.table || ""} style={{ borderSpacing: 0 }}>
          {!isLoading && (
            <thead className={classNames.thead || ""}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, headerIndex) => {
                    const isPinnedLeft =
                      header.column.columnDef.meta?.pinned === "left";
                    const isPinnedRight =
                      header.column.columnDef.meta?.pinned === "right";

                    const leftOffset = calculateLeftOffset(
                      headerIndex,
                      isPinnedLeft,
                      leftPinnedColumns,
                      columns,
                    );
                    const rightOffset = calculateRightOffset(
                      headerIndex,
                      isPinnedRight,
                      rightPinnedColumns,
                      columns,
                    );
                    const width = getColumnWidth(header.column);

                    // 计算列位置和阴影类名（表头）
                    const {
                      leftShadowClass: leftShadowClassHeader,
                      rightShadowClass: rightShadowClassHeader,
                    } = calculateColumnShadowClasses(
                      header.column.id,
                      headerIndex,
                      isPinnedLeft,
                      isPinnedRight,
                      leftPinnedColumns,
                      rightPinnedColumns,
                      columns,
                      isScrolledFromStart,
                      isScrolledToEnd,
                      classNames.mask,
                    );

                    const thClassName = cn(
                      classNames.th,
                      (isPinnedLeft || isPinnedRight) && classNames.thPinned,
                      header.column.columnDef.meta?.headerClassName,
                      leftShadowClassHeader,
                      rightShadowClassHeader,
                    );

                    return (
                      <th
                        key={header.id}
                        className={thClassName}
                        style={{
                          ...(width && {
                            width:
                              typeof width === "number" ? `${width}px` : width,
                            minWidth:
                              typeof width === "number" ? `${width}px` : width,
                          }),
                          ...(isPinnedLeft && { left: leftOffset }),
                          ...(isPinnedRight && { right: rightOffset }),
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          )}

          <tbody className={classNames.tbody || ""}>
            {topRows.length > 0 && renderRows(topRows, true)}
            {renderRows(centerRows, false)}
            {bottomRows.length > 0 && renderRows(bottomRows, false)}
          </tbody>
        </table>
      </div>

      {isScrolled && rightPinnedColumns.length === 0 && (
        <div
          className={
            classNames?.mask?.container || generateContainerShadowClass()
          }
        />
      )}
    </div>
  );
}
